import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/services/supabaseClient';
import {
  activateBudgetPacingPersistence,
  clearBudgetPacingPersistence,
} from '@/lib/budgetPacingPersist';

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

export interface MFATotpFactor {
  id: string;
  status: 'verified' | 'unverified';
  friendly_name?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null; confirmEmail: boolean }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  changePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  // MFA
  getMFAFactors: () => Promise<MFATotpFactor[]>;
  enrollMFA: () => Promise<{ factorId: string; qrCode: string; secret: string; error: Error | null }>;
  verifyMFAEnrollment: (factorId: string, code: string) => Promise<{ error: Error | null }>;
  unenrollMFA: (factorId: string) => Promise<{ error: Error | null }>;
  getMFALevel: () => Promise<{ currentLevel: string; nextLevel: string }>;
  verifyMFALogin: (factorId: string, code: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      // Budgets-page warm cache is per signed-in user; nothing is read before this.
      activateBudgetPacingPersistence(s?.user?.id ?? null);
      setIsLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, s) => {
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user?.id) activateBudgetPacingPersistence(s.user.id);
        else clearBudgetPacingPersistence();
      },
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) return { error: error as Error | null, confirmEmail: false };
    // Supabase returns a fake user with empty identities for existing emails
    // to prevent user enumeration. Show "check your email" in both cases.
    const needsConfirmation = !data.session;
    return { error: null, confirmEmail: needsConfirmation };
  }, []);

  const signOut = useCallback(async () => {
    // Before the network call: cached spend figures must not survive a
    // sign-out that fails halfway, on a shared machine least of all.
    clearBudgetPacingPersistence();
    await supabase.auth.signOut();
    if (DISABLE_AUTH) {
      localStorage.removeItem('supabase.auth.token');
      Object.keys(localStorage)
        .filter((key) => key.startsWith('sb-') || key.includes('supabase'))
        .forEach((key) => localStorage.removeItem(key));
    }
    setSession(null);
    setUser(null);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { prompt: 'select_account' },
      },
    });
    return { error: error as Error | null };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/password-reset`,
    });
    return { error: error as Error | null };
  }, []);

  const changePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error: error as Error | null };
  }, []);

  // ---------------------------------------------------------------------------
  // MFA / TOTP (Supabase native)
  // ---------------------------------------------------------------------------

  const getMFAFactors = useCallback(async (): Promise<MFATotpFactor[]> => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error || !data) return [];
    return (data.totp ?? []).map((f) => ({
      id: f.id,
      status: f.status as 'verified' | 'unverified',
      friendly_name: f.friendly_name,
    }));
  }, []);

  const enrollMFA = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (error || !data) {
      return { factorId: '', qrCode: '', secret: '', error: error as Error | null };
    }
    return {
      factorId: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
      error: null,
    };
  }, []);

  const verifyMFAEnrollment = useCallback(async (factorId: string, code: string) => {
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    return { error: error as Error | null };
  }, []);

  const unenrollMFA = useCallback(async (factorId: string) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    return { error: error as Error | null };
  }, []);

  const getMFALevel = useCallback(async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    return {
      currentLevel: data?.currentLevel ?? 'aal1',
      nextLevel: data?.nextLevel ?? 'aal1',
    };
  }, []);

  const verifyMFALogin = useCallback(async (factorId: string, code: string) => {
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    return { error: error as Error | null };
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signIn, signUp, signInWithGoogle, resetPassword, changePassword, signOut, getMFAFactors, enrollMFA, verifyMFAEnrollment, unenrollMFA, getMFALevel, verifyMFALogin }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
